import { useEffect, useState } from "react";
import { Mail, Globe, CreditCard, Database } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Loader } from "@/components/portal/ui";

const AREA_META = [
  { key: "email", icon: Mail, label: "ready.col.email" },
  { key: "wordpress", icon: Globe, label: "ready.col.wordpress" },
  { key: "payments", icon: CreditCard, label: "ready.col.payments" },
  { key: "data", icon: Database, label: "ready.col.data" },
];

const STATE = {
  configured:            { tone: "ok",    label: "ready.state.configured" },
  connected:             { tone: "ok",    label: "ready.state.connected" },
  live:                  { tone: "ok",    label: "ready.state.live" },
  synthetic:             { tone: "info",  label: "ready.state.synthetic" },
  demo:                  { tone: "warn",  label: "ready.state.demo" },
  mixed:                 { tone: "warn",  label: "ready.state.mixed" },
  not_configured:        { tone: "warn",  label: "ready.state.not_configured" },
  not_connected:         { tone: "warn",  label: "ready.state.not_connected" },
  gateway_not_connected: { tone: "warn",  label: "ready.state.gateway_not_connected" },
  empty:                 { tone: "muted", label: "ready.state.empty" },
  error:                 { tone: "err",   label: "ready.state.error" },
};
const TONE = { ok: "#34d399", warn: "#fbbf24", info: "#60a5fa", err: "#f87171", muted: "#a1a1aa" };

function Chip({ state, t }) {
  const s = STATE[state] || STATE.not_configured;
  const c = TONE[s.tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
      style={{ color: c, borderColor: `${c}55`, background: `${c}14` }}
      aria-label={t(s.label)}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} /> {t(s.label)}
    </span>
  );
}

export function ProductionHealthMatrix() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/control/readiness").then(({ data }) => setData(data)).catch(() => setData({ portals: [] }));
  }, []);
  if (!data) return <Loader />;
  const portals = data.portals || [];
  return (
    <div data-testid="production-health-matrix" className="overflow-hidden rounded-lg border border-zinc-800/60 bg-[#18181B]">
      <div className="border-b border-zinc-800/60 px-5 py-4">
        <h2 className="font-heading text-lg font-bold tracking-tight text-zinc-100">{t("ready.title")}</h2>
        <p className="mt-0.5 text-xs text-zinc-500">{t("ready.sub")}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#18181B]">
              <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{t("ready.col.portal")}</th>
              {AREA_META.map((a) => {
                const Icon = a.icon;
                return (
                  <th key={a.key} className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                    <span className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {t(a.label)}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {portals.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-500" data-testid="readiness-empty">{t("matrix.empty")}</td></tr>
            )}
            {portals.map((p) => (
              <tr key={p.portal_key} data-testid={`readiness-row-${p.portal_key}`} className="border-b border-zinc-800/60 last:border-0">
                <td className="px-5 py-3">
                  <div className="font-heading text-sm font-semibold text-zinc-100">{p.name}</div>
                  <div className="text-[11px] text-zinc-500">{p.portal_key}</div>
                </td>
                {AREA_META.map((a) => (
                  <td key={a.key} className="px-3 py-3 text-center" data-testid={`readiness-${p.portal_key}-${a.key}`}>
                    <Chip state={p.areas?.[a.key]} t={t} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-zinc-800/60 px-5 py-3 text-[11px] text-zinc-500">{t("ready.legend")}</div>
    </div>
  );
}
